import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiMoreHorizontal } from "react-icons/fi";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { translateReservationStatus } from "../utils/pmsLanguages";
import ReservationGroupView from "../reservations/ReservationGroupView";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import { readStoredUser } from "../utils/sessionUser";
import "../FrontDesk.css";
import { getBookerDisplayName, getBookerContactName, isCompanyBooker, requiresCompanyBookerContact } from "../reservations/bookerPresentation";

const PAGE_SIZE = 200;
const FUTURE_WINDOW_DAYS = 7;
const FRONT_DESK_BASE_TABLE_WIDTH = 1450;
const FRONT_DESK_MIN_ADAPTIVE_SCALE = 0.74;
const FRONT_DESK_SCALE_EPSILON = 0.004;
const FRONT_DESK_DEFAULT_CHECK_IN_SETTINGS = {
  required_customer_fields_check_in: {},
  required_customer_roles_check_in: ["main_guest"],
  require_full_payment_for_check_in: false,
};

const FRONT_DESK_COPY = {
  en: {
    locale: "en-GB",
    section: "Front Desk",
    checkIns: "Check ins",
    inHouse: "In House",
    inHouseGuests: "In House guests",
    checkOuts: "Check outs",
    sortedByCheckIn: "Sorted by check-in date",
    sortedByInHouse: "Current checked-in guests",
    sortedByCheckOut: "Sorted by check-out date",
    showOld: "Show old",
    hideOld: "Hide old",
    showFuture: "Show future",
    hideFuture: "Hide future",
    showOtherDates: "Show other dates",
    hideOtherDates: "Hide other dates",
    dateRangeLabel: "Date range",
    oldRange: "Old range",
    futureRange: "Future range",
    dateFrom: "From",
    dateTo: "To",
    visible: "Visible",
    today: "Today",
    overdue: "Overdue",
    upcoming: "Upcoming",
    future: "Future",
    hiddenOld: "Hidden old",
    hiddenFuture: "Hidden future",
    reservation: "Reservation",
    room: "Room",
    checkIn: "Check-in",
    checkOut: "Check-out",
    occupancy: "Occupancy",
    status: "Status",
    total: "Total",
    paid: "Paid",
    balance: "Balance",
    readiness: "Check-in prep",
    readyForCheckIn: "Ready",
    missingRoomNumber: "Room Number",
    missingStayDates: "Stay dates",
    missingOccupancy: "Occupancy",
    missingBooker: "Booker",
    missingBookerData: "Booker data",
    missingMainGuest: "Main guest",
    missingMainGuestData: "Main guest data",
    missingAccompanyingGuests: "Accompanying guests",
    missingAccompanyingGuestData: "Accompanying guest data",
    missingChildGuests: "Child guests",
    missingChildGuestData: "Child guest data",
    missingFullPayment: "Full payment",
    moreMissing: (count) => `+${count} more`,
    action: "Actions",
    noGuest: "No guest",
    unassigned: "Unassigned",
    noDate: "No date",
    notAvailable: "N/A",
    scheduled: "Scheduled",
    loadingTitle: "Loading front desk...",
    loadingCopy: "Please wait while reservations are loaded.",
    errorTitle: "Could not load front desk.",
    errorCopy: "Reservations could not be loaded right now.",
    noBusinessUnitTitle: "No business unit selected.",
    noBusinessUnitCopy: "Select a business unit to load check-ins and check-outs.",
    permissionTitle: "No front desk access.",
    permissionCopy: "Your user does not have permission to view this screen.",
    emptyTitle: "No reservations found.",
    emptyCopy: "There are no reservations matching this front desk view.",
    emptyHiddenTitle: "No visible reservations.",
    emptyHiddenCopy: "Show old or future reservations if you want to include records outside the next 7 days.",
    emptyDateRangeTitle: "No reservations in range.",
    emptyDateRangeCopy: "Try a different start or end date.",
    visibleCount: (visible, total) => `${visible} of ${total} reservations visible`,
    rowCount: (count) => `${count} reservations`,
    nights: (count) => `${count} night${count === 1 ? "" : "s"}`,
    openReservation: "Open reservation",
    viewGridAction: "Grid",
    occupancyGridAction: "View occupancy grid",
    checkInAction: "Check in",
    checkInSuccess: (locator) => `Check-in completed for ${locator}.`,
    checkInError: "Could not complete this check-in.",
    checkInMissingTitle: "Required check-in data missing",
    checkInMissingMessage: (missing) => `Please fill the required check-in data before completing check-in: ${missing}.`,
    checkInMissingConfirm: "Open reservation",
    checkInMissingCancel: "Stay here",
    noShowAction: "No show",
    revertNoShowAction: "Revert No Show",
    savingAction: "Saving...",
    loadingActions: "Loading...",
    adultsShort: "A",
    childrenShort: "C",
    babiesShort: "B",
  },
  es: {
    locale: "es-ES",
    section: "Front Desk",
    checkIns: "Check ins",
    inHouse: "In House",
    inHouseGuests: "Huespedes in house",
    checkOuts: "Check outs",
    sortedByCheckIn: "Ordenado por fecha de check in",
    sortedByInHouse: "Huespedes actualmente checked-in",
    sortedByCheckOut: "Ordenado por fecha de check out",
    showOld: "Mostrar antiguos",
    hideOld: "Ocultar antiguos",
    showFuture: "Mostrar futuros",
    hideFuture: "Ocultar futuros",
    showOtherDates: "Mostrar otras fechas",
    hideOtherDates: "Ocultar otras fechas",
    dateRangeLabel: "Rango",
    oldRange: "Rango antiguos",
    futureRange: "Rango futuros",
    dateFrom: "Desde",
    dateTo: "Hasta",
    visible: "Visibles",
    today: "Hoy",
    overdue: "Vencidos",
    upcoming: "Proximos",
    future: "Futuro",
    hiddenOld: "Antiguos ocultos",
    hiddenFuture: "Futuros ocultos",
    reservation: "Reserva",
    room: "Habitacion",
    checkIn: "Check in",
    checkOut: "Check out",
    occupancy: "Ocupacion",
    status: "Estado",
    total: "Total",
    paid: "Pagado",
    balance: "Pendiente",
    readiness: "Preparacion check-in",
    readyForCheckIn: "Listo",
    missingRoomNumber: "Room Number",
    missingStayDates: "Fechas estancia",
    missingOccupancy: "Ocupacion",
    missingBooker: "Booker",
    missingBookerData: "Booker data",
    missingMainGuest: "Main guest",
    missingMainGuestData: "Main guest data",
    missingAccompanyingGuests: "Acompanantes",
    missingAccompanyingGuestData: "Accompanying guest data",
    missingChildGuests: "Children",
    missingChildGuestData: "Child guest data",
    missingFullPayment: "Pago completo",
    moreMissing: (count) => `+${count} mas`,
    action: "Acciones",
    noGuest: "Sin huesped",
    unassigned: "Sin asignar",
    noDate: "Sin fecha",
    notAvailable: "N/D",
    scheduled: "Programado",
    loadingTitle: "Cargando front desk...",
    loadingCopy: "Espera mientras se cargan las reservas.",
    errorTitle: "No se pudo cargar front desk.",
    errorCopy: "Ahora mismo no se han podido cargar las reservas.",
    noBusinessUnitTitle: "No hay unidad de negocio seleccionada.",
    noBusinessUnitCopy: "Selecciona una unidad de negocio para cargar check ins y check outs.",
    permissionTitle: "Sin acceso a front desk.",
    permissionCopy: "Tu usuario no tiene permisos para ver esta pantalla.",
    emptyTitle: "No hay reservas.",
    emptyCopy: "No hay reservas que encajen en esta vista de front desk.",
    emptyHiddenTitle: "No hay reservas visibles.",
    emptyHiddenCopy: "Muestra antiguos o futuros si quieres incluir reservas fuera de los proximos 7 dias.",
    emptyDateRangeTitle: "No hay reservas en el rango.",
    emptyDateRangeCopy: "Prueba con otra fecha inicial o final.",
    visibleCount: (visible, total) => `${visible} de ${total} reservas visibles`,
    rowCount: (count) => `${count} reservas`,
    nights: (count) => `${count} noche${count === 1 ? "" : "s"}`,
    openReservation: "Abrir reserva",
    viewGridAction: "Grid",
    occupancyGridAction: "Ver occupancy grid",
    checkInAction: "Check in",
    checkInSuccess: (locator) => `Check in completado para ${locator}.`,
    checkInError: "No se pudo completar este check in.",
    checkInMissingTitle: "Required check-in data missing",
    checkInMissingMessage: (missing) => `Please fill the required check-in data before completing check-in: ${missing}.`,
    checkInMissingConfirm: "Open reservation",
    checkInMissingCancel: "Stay here",
    noShowAction: "No show",
    revertNoShowAction: "Revertir no show",
    savingAction: "Guardando...",
    loadingActions: "Cargando...",
    adultsShort: "A",
    childrenShort: "N",
    babiesShort: "B",
  },
  ca: {
    locale: "ca-ES",
    section: "Front Desk",
    checkIns: "Check ins",
    inHouse: "In House",
    inHouseGuests: "Hostes in house",
    checkOuts: "Check outs",
    sortedByCheckIn: "Ordenat per data de check in",
    sortedByInHouse: "Hostes actualment checked-in",
    sortedByCheckOut: "Ordenat per data de check out",
    showOld: "Mostrar antics",
    hideOld: "Ocultar antics",
    showFuture: "Mostrar futurs",
    hideFuture: "Ocultar futurs",
    showOtherDates: "Mostrar altres dates",
    hideOtherDates: "Amagar altres dates",
    dateRangeLabel: "Rang",
    oldRange: "Rang antics",
    futureRange: "Rang futurs",
    dateFrom: "Des de",
    dateTo: "Fins a",
    visible: "Visibles",
    today: "Avui",
    overdue: "Vencuts",
    upcoming: "Propers",
    future: "Futur",
    hiddenOld: "Antics ocults",
    hiddenFuture: "Futurs ocults",
    reservation: "Reserva",
    room: "Habitacio",
    checkIn: "Check in",
    checkOut: "Check out",
    occupancy: "Ocupacio",
    status: "Estat",
    total: "Total",
    paid: "Pagat",
    balance: "Pendent",
    readiness: "Preparacio check-in",
    readyForCheckIn: "Llest",
    missingRoomNumber: "Room Number",
    missingStayDates: "Dates estada",
    missingOccupancy: "Ocupacio",
    missingBooker: "Booker",
    missingBookerData: "Booker data",
    missingMainGuest: "Main guest",
    missingMainGuestData: "Main guest data",
    missingAccompanyingGuests: "Acompanyants",
    missingAccompanyingGuestData: "Accompanying guest data",
    missingChildGuests: "Children",
    missingChildGuestData: "Child guest data",
    missingFullPayment: "Pagament complet",
    moreMissing: (count) => `+${count} mes`,
    action: "Accions",
    noGuest: "Sense hoste",
    unassigned: "Sense assignar",
    noDate: "Sense data",
    notAvailable: "N/D",
    scheduled: "Programat",
    loadingTitle: "Carregant front desk...",
    loadingCopy: "Espera mentre es carreguen les reserves.",
    errorTitle: "No s'ha pogut carregar front desk.",
    errorCopy: "Ara mateix no s'han pogut carregar les reserves.",
    noBusinessUnitTitle: "No hi ha unitat de negoci seleccionada.",
    noBusinessUnitCopy: "Selecciona una unitat de negoci per carregar check ins i check outs.",
    permissionTitle: "Sense acces a front desk.",
    permissionCopy: "El teu usuari no te permisos per veure aquesta pantalla.",
    emptyTitle: "No hi ha reserves.",
    emptyCopy: "No hi ha reserves que encaixin en aquesta vista de front desk.",
    emptyHiddenTitle: "No hi ha reserves visibles.",
    emptyHiddenCopy: "Mostra antics o futurs si vols incloure reserves fora dels propers 7 dies.",
    emptyDateRangeTitle: "No hi ha reserves en el rang.",
    emptyDateRangeCopy: "Prova amb una altra data inicial o final.",
    visibleCount: (visible, total) => `${visible} de ${total} reserves visibles`,
    rowCount: (count) => `${count} reserves`,
    nights: (count) => `${count} nit${count === 1 ? "" : "s"}`,
    openReservation: "Obrir reserva",
    viewGridAction: "Grid",
    occupancyGridAction: "Veure occupancy grid",
    checkInAction: "Check in",
    checkInSuccess: (locator) => `Check in completat per a ${locator}.`,
    checkInError: "No s'ha pogut completar aquest check in.",
    checkInMissingTitle: "Required check-in data missing",
    checkInMissingMessage: (missing) => `Please fill the required check-in data before completing check-in: ${missing}.`,
    checkInMissingConfirm: "Open reservation",
    checkInMissingCancel: "Stay here",
    noShowAction: "No show",
    revertNoShowAction: "Revertir no show",
    savingAction: "Guardant...",
    loadingActions: "Carregant...",
    adultsShort: "A",
    childrenShort: "N",
    babiesShort: "B",
  },
};

function normalizeIsoDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function rowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.rows) ? payload.rows : [];
}

function normalizeStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function reservationStatusTone(status) {
  switch (normalizeStatus(status)) {
    case "confirmed": return "confirmed";
    case "no show":
    case "noshow": return "no-show";
    case "checked in":
    case "in house": return "checked-in";
    default: return "neutral";
  }
}

function addIsoDays(isoDate, days) {
  if (!isoDate) return "";
  const date = new Date(`${normalizeIsoDate(isoDate)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function buildDefaultVisibleRange(baseIsoDate) {
  return {
    from: baseIsoDate,
    to: addIsoDays(baseIsoDate, FUTURE_WINDOW_DAYS),
  };
}

function normalizeDateRange(range, fallbackFrom, fallbackTo) {
  const rawFrom = normalizeIsoDate(range?.from) || fallbackFrom;
  const rawTo = normalizeIsoDate(range?.to) || fallbackTo;
  if (!rawFrom && !rawTo) return { from: "", to: "" };
  if (!rawFrom) return { from: rawTo, to: rawTo };
  if (!rawTo) return { from: rawFrom, to: rawFrom };
  return compareIsoDates(rawFrom, rawTo) <= 0
    ? { from: rawFrom, to: rawTo }
    : { from: rawTo, to: rawFrom };
}

function compareIsoDates(a, b) {
  const safeA = a || "9999-12-31";
  const safeB = b || "9999-12-31";
  if (safeA < safeB) return -1;
  if (safeA > safeB) return 1;
  return 0;
}

function nightsDiff(checkIn, checkOut) {
  const start = new Date(`${normalizeIsoDate(checkIn)}T00:00:00`);
  const end = new Date(`${normalizeIsoDate(checkOut)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
}

function reservationNights(reservation) {
  const value = Number(reservation?.room_nights ?? reservation?.nights ?? 0);
  if (Number.isFinite(value) && value > 0) return Math.round(value);
  return nightsDiff(reservation?.check_in_date, reservation?.check_out_date);
}

function readCurrentUser() {
  return readStoredUser();
}

function reservationLocator(reservation) {
  if (reservation?.locator) return reservation.locator;
  if (reservation?.group_locator) {
    return reservation.group_index
      ? `${reservation.group_locator}-${reservation.group_index}`
      : reservation.group_locator;
  }
  if (reservation?.external_partner_locator) return reservation.external_partner_locator;
  if (reservation?.id) return `RES-${reservation.id}`;
  return "";
}

function reservationGuestName(reservation, copy) {
  const guests = Array.isArray(reservation?.guests) ? reservation.guests : [];
  const chosenGuest =
    guests.find((guest) => guest?.is_main_guest) ||
    guests.find((guest) => guest?.is_booker) ||
    guests[0];

  const customer = chosenGuest?.customer || chosenGuest || {};
  const firstName = customer?.first_name || customer?.name || "";
  const lastName = customer?.last_name || customer?.surname || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || getBookerDisplayName(reservation) || copy.noGuest;
}

function reservationRoomCategory(reservation, copy) {
  return (
    reservation?.room_category?.name ||
    reservation?.effective_room_category?.name ||
    reservation?.room?.room_category?.name ||
    reservation?.room_type_name ||
    copy.notAvailable
  );
}

function reservationRoomNumber(reservation, copy) {
  return (
    reservation?.effective_room?.room_number ||
    reservation?.effective_room_number ||
    reservation?.room?.room_number ||
    copy.unassigned
  );
}

function safeSettingsObject(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string") return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : fallback;
  } catch (_error) {
    return fallback;
  }
}

function normalizeCheckInRoleKey(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "mainguest" || key === "main_guest") return "main_guest";
  if (key === "accompanying_guest" || key === "accompanying_guests") return "accompanying";
  if (key === "children" || key === "children_guest" || key === "children_guests") return "child";
  if (key === "booker" || key === "accompanying" || key === "child") return key;
  return "";
}

export function normalizeCheckInSettings(rawSettings) {
  const source = rawSettings || {};
  const rawRoles = Array.isArray(source.required_customer_roles_check_in)
    ? source.required_customer_roles_check_in
    : FRONT_DESK_DEFAULT_CHECK_IN_SETTINGS.required_customer_roles_check_in;
  const rawFields = safeSettingsObject(source.required_customer_fields_check_in, {});
  const fieldRuleRoles = ["booker", "main_guest", "accompanying", "child"].filter((role) => {
    const directFields = Array.isArray(rawFields[role]) ? rawFields[role] : [];
    const aliasFields =
      role === "accompanying"
        ? [
            ...(Array.isArray(rawFields.accompanying_guest) ? rawFields.accompanying_guest : []),
            ...(Array.isArray(rawFields.accompanying_guests) ? rawFields.accompanying_guests : []),
          ]
        : role === "child"
          ? [
              ...(Array.isArray(rawFields.children) ? rawFields.children : []),
              ...(Array.isArray(rawFields.children_guest) ? rawFields.children_guest : []),
              ...(Array.isArray(rawFields.children_guests) ? rawFields.children_guests : []),
            ]
          : [];
    return directFields.length > 0 || aliasFields.length > 0;
  });
  const roles = [
    "main_guest",
    ...rawRoles
      .map(normalizeCheckInRoleKey)
      .filter((role) => role && role !== "main_guest"),
    ...fieldRuleRoles,
  ];
  const uniqueRoles = [...new Set(roles)];

  return {
    require_company_booker_contact: requiresCompanyBookerContact(source),
    required_customer_roles_check_in: uniqueRoles,
    required_customer_fields_check_in: {
      booker: [...new Set(Array.isArray(rawFields.booker) ? rawFields.booker : [])],
      main_guest: [...new Set(Array.isArray(rawFields.main_guest) ? rawFields.main_guest : [])],
      accompanying: [...new Set([
        ...(Array.isArray(rawFields.accompanying) ? rawFields.accompanying : []),
        ...(Array.isArray(rawFields.accompanying_guest) ? rawFields.accompanying_guest : []),
        ...(Array.isArray(rawFields.accompanying_guests) ? rawFields.accompanying_guests : []),
      ])],
      child: [...new Set([
        ...(Array.isArray(rawFields.child) ? rawFields.child : []),
        ...(Array.isArray(rawFields.children) ? rawFields.children : []),
        ...(Array.isArray(rawFields.children_guest) ? rawFields.children_guest : []),
        ...(Array.isArray(rawFields.children_guests) ? rawFields.children_guests : []),
      ])],
    },
    require_full_payment_for_check_in:
      source.require_full_payment_for_check_in === true ||
      String(source.require_full_payment_for_check_in).toLowerCase() === "true",
  };
}

function hasAssignedRoomNumber(reservation) {
  if (
    reservation?.effective_room?.room_number ||
    reservation?.effective_room_number ||
    reservation?.room?.room_number ||
    reservation?.room_number
  ) {
    return true;
  }

  const assignments = Array.isArray(reservation?.room_assignments)
    ? reservation.room_assignments
    : [];
  return assignments.some((assignment) =>
    Boolean(assignment?.room?.room_number || assignment?.room_number || assignment?.room_id)
  );
}

function checkInReadinessGuestRows(reservation) {
  if (Array.isArray(reservation?.check_in_readiness?.guests)) {
    return reservation.check_in_readiness.guests;
  }
  if (Array.isArray(reservation?.check_in_guest_profiles)) {
    return reservation.check_in_guest_profiles;
  }
  return Array.isArray(reservation?.guests) ? reservation.guests : [];
}

function guestCustomer(row) {
  const customer = row?.customer || row?.Customer || row;
  return customer && typeof customer === "object" ? customer : null;
}

function roleCustomers(reservation, role) {
  const rows = checkInReadinessGuestRows(reservation);
  const filteredRows = rows.filter((row) => {
    if (role === "main_guest") return Boolean(row?.is_main_guest);
    if (role === "child") return Boolean(row?.is_child);
    if (role === "accompanying") return !row?.is_main_guest && !row?.is_child;
    return false;
  });
  return filteredRows.map(guestCustomer).filter(Boolean);
}

function reservationBookerCustomer(reservation) {
  const directBooker =
    reservation?.check_in_readiness?.booker ||
    reservation?.booker ||
    reservation?.group?.booker ||
    null;
  if (directBooker) return directBooker;

  const bookerRow = checkInReadinessGuestRows(reservation).find((row) => row?.is_booker);
  return guestCustomer(bookerRow);
}

function customerFieldIsComplete(customer, fieldName) {
  if (!customer) return false;
  if (fieldName === "is_vip") {
    return customer.is_vip !== undefined && customer.is_vip !== null;
  }

  const value = customer?.[fieldName];
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return true;
  return String(value).trim().length > 0;
}

function customerHasRequiredFields(customer, fields) {
  const requiredFields = Array.isArray(fields) ? fields : [];
  if (!requiredFields.length) return true;
  return requiredFields.every((fieldName) => customerFieldIsComplete(customer, fieldName));
}

function addMissingReadinessItem(items, key, label) {
  if (items.some((item) => item.key === key)) return;
  items.push({ key, label });
}

export function buildCheckInReadiness(reservation, settings, balance, copy) {
  const normalizedSettings = normalizeCheckInSettings(settings);
  const missing = [];
  const requiredRoles = new Set(normalizedSettings.required_customer_roles_check_in);
  const requiredFields = normalizedSettings.required_customer_fields_check_in;

  if (!hasAssignedRoomNumber(reservation)) {
    addMissingReadinessItem(missing, "room_number", copy.missingRoomNumber);
  }

  const checkInDate = normalizeIsoDate(reservation?.check_in_date);
  const checkOutDate = normalizeIsoDate(reservation?.check_out_date);
  if (!checkInDate || !checkOutDate || compareIsoDates(checkInDate, checkOutDate) >= 0) {
    addMissingReadinessItem(missing, "stay_dates", copy.missingStayDates);
  }

  const adults = Number(reservation?.adults ?? reservation?.num_adults);
  const children = Number(reservation?.children ?? reservation?.num_children ?? 0);
  if (!Number.isFinite(adults) || adults < 1 || !Number.isFinite(children) || children < 0) {
    addMissingReadinessItem(missing, "occupancy", copy.missingOccupancy);
  }

  if (isCompanyBooker(reservation)) {
    if (!getBookerDisplayName(reservation)) {
      addMissingReadinessItem(missing, "booker_data", copy.missingBookerData);
    }
    if (requiresCompanyBookerContact(settings) && !getBookerContactName(reservation)) {
      addMissingReadinessItem(missing, "booker_contact", copy.missingBookerData);
    }
  } else {
    const booker = reservationBookerCustomer(reservation);
    if (requiredRoles.has("booker") && !booker) {
      addMissingReadinessItem(missing, "booker", copy.missingBooker);
    } else if (booker && !customerHasRequiredFields(booker, requiredFields.booker)) {
      addMissingReadinessItem(missing, "booker_data", copy.missingBookerData);
    }
  }

  const mainGuests = roleCustomers(reservation, "main_guest");
  const mainGuest = mainGuests[0] || null;
  if (requiredRoles.has("main_guest") && !mainGuest) {
    addMissingReadinessItem(missing, "main_guest", copy.missingMainGuest);
  } else if (mainGuest && !customerHasRequiredFields(mainGuest, requiredFields.main_guest)) {
    addMissingReadinessItem(missing, "main_guest_data", copy.missingMainGuestData);
  }

  const accompanyingGuests = roleCustomers(reservation, "accompanying");
  const expectedAccompanyingGuests = Number.isFinite(adults) ? Math.max(adults - 1, 0) : 0;
  if (requiredRoles.has("accompanying") && accompanyingGuests.length < expectedAccompanyingGuests) {
    addMissingReadinessItem(missing, "accompanying_guests", copy.missingAccompanyingGuests);
  }
  if (
    accompanyingGuests.length > 0 &&
    requiredFields.accompanying.length > 0 &&
    accompanyingGuests.some((customer) => !customerHasRequiredFields(customer, requiredFields.accompanying))
  ) {
    addMissingReadinessItem(
      missing,
      "accompanying_guest_data",
      copy.missingAccompanyingGuestData
    );
  }

  const childGuests = roleCustomers(reservation, "child");
  const expectedChildGuests = Number.isFinite(children) ? Math.max(children, 0) : 0;
  if (requiredRoles.has("child") && childGuests.length < expectedChildGuests) {
    addMissingReadinessItem(missing, "child_guests", copy.missingChildGuests);
  }
  if (
    childGuests.length > 0 &&
    requiredFields.child.length > 0 &&
    childGuests.some((customer) => !customerHasRequiredFields(customer, requiredFields.child))
  ) {
    addMissingReadinessItem(missing, "child_guest_data", copy.missingChildGuestData);
  }

  if (
    normalizedSettings.require_full_payment_for_check_in &&
    Number.isFinite(Number(balance)) &&
    Number(balance) > 0.005
  ) {
    addMissingReadinessItem(missing, "full_payment", copy.missingFullPayment);
  }

  return {
    missing,
    isReady: missing.length === 0,
    title: missing.map((item) => item.label).join(", "),
  };
}

function paidAmount(reservation) {
  const directValue = Number(
    reservation?.paid_amount ??
      reservation?.paid ??
      reservation?.amount_paid ??
      reservation?.payments_total
  );
  if (Number.isFinite(directValue)) return directValue;

  const payments = Array.isArray(reservation?.payments) ? reservation.payments : [];
  return payments.reduce((sum, payment) => {
    const amount = Number(payment?.amount ?? payment?.paid_amount ?? 0);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
}

function amountShapeToNumber(value) {
  if (value && typeof value === "object" && value.amount != null) {
    const amount = Number(value.amount);
    return Number.isFinite(amount) ? amount : 0;
  }

  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function reservationPaymentBalance(reservation, balancesByGroupId) {
  const groupId = Number(reservation?.reservation_group_id);
  if (!groupId) return null;

  const groupBalance = balancesByGroupId?.[groupId] || null;
  const rows = Array.isArray(groupBalance?.reservations) ? groupBalance.reservations : [];
  return rows.find((row) => Number(row.reservation_id) === Number(reservation?.id)) || null;
}

function reservationCurrency(reservation) {
  return (
    reservation?.currency_code ||
    reservation?.currency?.code ||
    reservation?.currency ||
    "EUR"
  );
}

function formatDateLabel(value, locale, fallback) {
  const isoDate = normalizeIsoDate(value);
  if (!isoDate) return fallback;
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return fallback;

  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "2-digit",
      month: "short",
    }).format(date);
  } catch (_error) {
    return isoDate;
  }
}

function refDateBucket(refDate, todayIso, futureCutoffIso) {
  if (!refDate) return "scheduled";
  if (refDate < todayIso) return "overdue";
  if (refDate === todayIso) return "today";
  if (futureCutoffIso && refDate > futureCutoffIso) return "future";
  return "upcoming";
}

async function fetchAllReservations(businessUnitId, signal, options = {}) {
  const { refField = "check_in_date", dateFrom = "", dateTo = "" } = options;
  const buildQueryString = (page) => {
    const params = new URLSearchParams();
    params.set("business_unit_id", String(businessUnitId));
    params.set("page", String(page));
    params.set("page_size", String(PAGE_SIZE));
    params.set("show_cancelled", "1");
    params.set("include_check_in_readiness", "1");
    if (dateFrom) params.set(`${refField}_from`, dateFrom);
    if (dateTo) params.set(`${refField}_to`, dateTo);
    return params.toString();
  };

  const first = await axios.get(
    `/api/reservations?${buildQueryString(1)}`,
    { signal }
  );
  const firstRows = rowsFromPayload(first?.data);
  const totalPages = Array.isArray(first?.data)
    ? 1
    : Math.max(1, Number(first?.data?.total_pages || 1));

  if (totalPages <= 1) return firstRows;

  const allRows = [...firstRows];
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await axios.get(
      `/api/reservations?${buildQueryString(page)}`,
      { signal }
    );
    allRows.push(...rowsFromPayload(next?.data));
  }

  return allRows;
}

function StateCard({ title, copy }) {
  return (
    <section className="fd-card fd-state">
      <h2>{title}</h2>
      <p>{copy}</p>
    </section>
  );
}

export default function FrontDesk({ forcedTab }) {
  const demoReadOnlyActive = isDemoMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { themeId, languageId, language } = usePmsTheme();
  const { formatFromBase } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const copy = FRONT_DESK_COPY[languageId] || FRONT_DESK_COPY.en;
  const locale = language?.locale || copy.locale;
  const businessUnitId = demoReadOnlyActive
    ? DEMO_BUSINESS_UNIT_ID
    : searchParams.get("business_unit_id");
  const currentUser = readCurrentUser();
  const permissions = currentUser?.permissions || {};
  const canViewCheckIns = Boolean(permissions["front_desk.view_check_in"]);
  const canViewCheckOuts = Boolean(permissions["front_desk.view_check_out"]);
  const canViewReservationDetails = Boolean(permissions["reservations.view_detail"]);
  const canViewPayments = Boolean(permissions["reservations.view_payments"]);
  const resolvedTab = useMemo(() => {
    if (forcedTab === "in-house" && canViewCheckOuts) return "in-house";
    if (forcedTab === "check-out" && canViewCheckOuts) return "check-out";
    if (forcedTab === "check-in" && canViewCheckIns) return "check-in";
    if (canViewCheckIns) return "check-in";
    if (canViewCheckOuts) return "in-house";
    return forcedTab === "check-out" || forcedTab === "in-house" ? "in-house" : "check-in";
  }, [canViewCheckIns, canViewCheckOuts, forcedTab]);

  const [reservations, setReservations] = useState([]);
  const [paymentBalancesByGroupId, setPaymentBalancesByGroupId] = useState({});
  const [frontDeskCheckInSettings, setFrontDeskCheckInSettings] = useState(() =>
    normalizeCheckInSettings(FRONT_DESK_DEFAULT_CHECK_IN_SETTINGS)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [showOtherDates, setShowOtherDates] = useState(false);
  const [dateRange, setDateRange] = useState(() =>
    buildDefaultVisibleRange((demoReadOnlyActive && demoReferenceDate()) || normalizeIsoDate(new Date().toISOString()))
  );
  const [reservationActionsLauncher, setReservationActionsLauncher] = useState(null);
  const [reservationActionsRefreshToken, setReservationActionsRefreshToken] = useState(0);
  const [frontDeskScale, setFrontDeskScale] = useState(1);
  const frontDeskRef = useRef(null);
  const dateFromInputRef = useRef(null);
  const crossTabRefreshToken = useCrossTabDataRefreshToken();

  const todayIso = (demoReadOnlyActive && demoReferenceDate()) || normalizeIsoDate(new Date().toISOString());
  const futureCutoffIso = addIsoDays(todayIso, FUTURE_WINDOW_DAYS);
  const activeDateBounds = useMemo(
    () =>
      normalizeDateRange(
        showOtherDates ? dateRange : buildDefaultVisibleRange(todayIso),
        todayIso,
        futureCutoffIso
      ),
    [dateRange, futureCutoffIso, showOtherDates, todayIso]
  );
  const activeRefField = resolvedTab === "check-in" ? "check_in_date" : "check_out_date";
  const requestDateFrom =
    !showOtherDates && (resolvedTab === "in-house" || resolvedTab === "check-out")
      ? ""
      : activeDateBounds.from;
  const requestDateTo =
    !showOtherDates && resolvedTab === "in-house"
      ? ""
      : !showOtherDates && resolvedTab === "check-out"
        ? todayIso
        : activeDateBounds.to;

  useEffect(() => {
    const root = frontDeskRef.current;
    if (!root) return undefined;

    let frameId = 0;

    const readAvailableWidth = () => {
      const styles = window.getComputedStyle(root);
      const paddingX =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);
      return Math.max(0, root.clientWidth - paddingX);
    };

    const resolveHeightScale = () => {
      const height = window.innerHeight || document.documentElement.clientHeight || 0;
      if (height <= 620) return 0.78;
      if (height <= 720) return 0.84;
      if (height <= 820) return 0.9;
      return 1;
    };

    const updateScale = () => {
      const widthScale = readAvailableWidth() / FRONT_DESK_BASE_TABLE_WIDTH;
      const nextScale = Math.max(
        FRONT_DESK_MIN_ADAPTIVE_SCALE,
        Math.min(1, widthScale || 1, resolveHeightScale())
      );

      setFrontDeskScale((currentScale) =>
        Math.abs(currentScale - nextScale) > FRONT_DESK_SCALE_EPSILON
          ? Number(nextScale.toFixed(3))
          : currentScale
      );
    };

    const scheduleUpdate = () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateScale);
    };

    scheduleUpdate();
    window.addEventListener("resize", scheduleUpdate);

    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    if (observer) observer.observe(root);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", scheduleUpdate);
      if (observer) observer.disconnect();
    };
  }, []);

  const openDateInputPicker = (inputRef) => {
    const element = inputRef?.current;
    if (!element) return;
    if (typeof element.showPicker === "function") {
      try {
        element.showPicker();
        return;
      } catch (_error) {
        // Fall back to focus when the browser blocks showPicker.
      }
    }
    element.focus();
  };

  const handleToggleOtherDates = () => {
    if (!showOtherDates) {
      setShowOtherDates(true);
      window.setTimeout(() => openDateInputPicker(dateFromInputRef), 0);
      return;
    }
    setShowOtherDates(false);
  };

  useEffect(() => {
    if (!businessUnitId) {
      setFrontDeskCheckInSettings(normalizeCheckInSettings(FRONT_DESK_DEFAULT_CHECK_IN_SETTINGS));
      return undefined;
    }

    const controller = new AbortController();

    axios
      .get("/api/hotel-settings", {
        params: { business_unit_id: businessUnitId },
        signal: controller.signal,
      })
      .then((response) => {
        if (controller.signal.aborted) return;
        setFrontDeskCheckInSettings(normalizeCheckInSettings(response?.data || {}));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn(
          "[FRONT_DESK][CHECK_IN_SETTINGS][WARN]",
          error?.response?.data || error?.message || error
        );
        setFrontDeskCheckInSettings(normalizeCheckInSettings(FRONT_DESK_DEFAULT_CHECK_IN_SETTINGS));
      });

    return () => {
      controller.abort();
    };
  }, [businessUnitId]);

  useEffect(() => {
    if (!businessUnitId) {
      setReservations([]);
      setPaymentBalancesByGroupId({});
      setLoadError("");
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");

    fetchAllReservations(businessUnitId, controller.signal, {
      refField: activeRefField,
      dateFrom: requestDateFrom,
      dateTo: requestDateTo,
    })
      .then((rows) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        setReservations(safeRows);

        if (!canViewPayments || demoReadOnlyActive) {
          setPaymentBalancesByGroupId({});
          return;
        }

        const groupIds = [...new Set(
          safeRows
            .map((reservation) => Number(reservation?.reservation_group_id))
            .filter((groupId) => Number.isInteger(groupId) && groupId > 0)
        )];

        if (!groupIds.length) {
          setPaymentBalancesByGroupId({});
          return;
        }

        axios.post(
          "/api/reservation-groups/payment-balances/batch",
          { reservation_group_ids: groupIds },
          { signal: controller.signal, skipCrossTabSync: true }
        )
          .then((res) => {
            if (!controller.signal.aborted) {
              setPaymentBalancesByGroupId(res?.data?.balances || {});
            }
          })
          .catch((error) => {
            if (controller.signal.aborted) return;
            console.warn("[FRONT_DESK][PAYMENT_BALANCES][WARN]", error?.response?.data || error?.message || error);
            setPaymentBalancesByGroupId({});
          });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setLoadError(error?.message || "load_error");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [
    activeRefField,
    businessUnitId,
    canViewPayments,
    crossTabRefreshToken,
    demoReadOnlyActive,
    requestDateFrom,
    requestDateTo,
    reservationActionsRefreshToken,
  ]);

  const board = useMemo(() => {
    const allowedStatuses =
      resolvedTab === "check-in"
        ? new Set(["confirmed", "no show", "noshow"])
        : new Set(["checked in", "in house"]);

    const rows = (reservations || [])
      .filter((reservation) => {
        const status = normalizeStatus(reservation?.status);
        if (status === "cancelled" || status === "canceled") {
          return false;
        }
        return allowedStatuses.has(status);
      })
      .map((reservation) => {
        const checkInDate = normalizeIsoDate(reservation?.check_in_date);
        const checkOutDate = normalizeIsoDate(reservation?.check_out_date);
        const refDate = resolvedTab === "check-in" ? checkInDate : checkOutDate;
        const paymentBalance = reservationPaymentBalance(reservation, paymentBalancesByGroupId);
        const total = paymentBalance
          ? amountShapeToNumber(paymentBalance.total)
          : Number(
              (demoReadOnlyActive ? reservation?.total_amount : undefined) ??
                reservation?.total_price ??
                reservation?.total ??
                reservation?.grand_total ??
                reservation?.reservation_total ??
                0
            );
        const paid = paymentBalance
          ? amountShapeToNumber(paymentBalance.allocated_paid)
          : paidAmount(reservation);
        const balance = paymentBalance
          ? amountShapeToNumber(paymentBalance.balance_due)
          : demoReadOnlyActive && reservation?.balance_due != null
            ? Number(reservation.balance_due)
            : total - paid;
        const checkInReadiness = buildCheckInReadiness(
          reservation,
          frontDeskCheckInSettings,
          balance,
          copy
        );
        const adults = Number(reservation?.adults ?? reservation?.num_adults ?? 0) || 0;
        const children = Number(reservation?.children ?? reservation?.num_children ?? 0) || 0;
        const babies = Number(reservation?.babies ?? reservation?.num_babies ?? 0) || 0;
        const bucket = refDateBucket(refDate, todayIso, futureCutoffIso);

        return {
          reservationId: reservation?.id,
          reservationGroupId: reservation?.reservation_group_id,
          locator: reservationLocator(reservation) || copy.notAvailable,
          guestName: reservationGuestName(reservation, copy),
          roomCategory: reservationRoomCategory(reservation, copy),
          roomNumber: reservationRoomNumber(reservation, copy),
          checkInDate,
          checkOutDate,
          refDate,
          bucket,
          statusTone: reservationStatusTone(reservation?.status),
          statusLabel:
            translateReservationStatus(reservation?.status, languageId) ||
            reservation?.status ||
            copy.notAvailable,
          total,
          paid,
          balance,
          checkInReadiness,
          currency: reservationCurrency(reservation),
          adults,
          children,
          babies,
          nights: reservationNights(reservation),
        };
      })
      .sort((left, right) => {
        const primary = compareIsoDates(left.refDate, right.refDate);
        if (primary !== 0) return primary;

        const secondary =
          resolvedTab === "check-in"
            ? compareIsoDates(left.checkOutDate, right.checkOutDate)
            : compareIsoDates(left.checkInDate, right.checkInDate);
        if (secondary !== 0) return secondary;

        return String(left.locator || "").localeCompare(String(right.locator || ""));
      });

    const overdueCount = rows.filter((row) => row.bucket === "overdue").length;
    const todayCount = rows.filter((row) => row.bucket === "today").length;
    const upcomingCount = rows.filter((row) => row.bucket === "upcoming").length;
    const futureCount = rows.filter((row) => row.bucket === "future").length;
    return {
      rows,
      totalRows: rows.length,
      overdueCount,
      todayCount,
      upcomingCount,
      futureCount,
    };
  }, [
    copy,
    demoReadOnlyActive,
    frontDeskCheckInSettings,
    futureCutoffIso,
    languageId,
    paymentBalancesByGroupId,
    reservations,
    resolvedTab,
    todayIso,
  ]);

  const activeTitle =
    resolvedTab === "check-in"
      ? copy.checkIns
      : resolvedTab === "in-house"
        ? copy.inHouseGuests
        : copy.checkOuts;
  const activeSubtitle =
    resolvedTab === "check-in"
      ? copy.sortedByCheckIn
      : resolvedTab === "in-house"
        ? copy.sortedByInHouse
        : copy.sortedByCheckOut;

  const openReservation = (row) => {
    if (!canViewReservationDetails) return;

    if (row.reservationGroupId) {
      navigate(
        `/reservation-group/${row.reservationGroupId}?mode=view&business_unit_id=${businessUnitId || ""}`
      );
      return;
    }

    navigate(`/reservation/${row.reservationId}`);
  };

  const openReservationActions = (row) => {
    if (!canViewReservationDetails) return;

    const reservationGroupId = Number(row?.reservationGroupId);
    const reservationId = Number(row?.reservationId);
    if (!reservationGroupId || !reservationId) return;

    setReservationActionsLauncher((current) => ({
      sessionId: Number(current?.sessionId || 0) + 1,
      reservationGroupId,
      reservationId,
      isReady: false,
    }));
  };

  const openOccupancyGrid = () => {
    const params = new URLSearchParams();
    if (businessUnitId) params.set("business_unit_id", String(businessUnitId));
    params.set("view", "grid");
    params.set("grid_view", "room_numbers");
    params.set("date_from", todayIso);
    navigate(`/reservations/list?${params.toString()}`);
  };

  const openTab = (tab) => {
    const basePath =
      tab === "check-out"
        ? "/front-desk/check-outs"
        : tab === "in-house"
          ? "/front-desk/in-house"
          : "/front-desk/check-ins";
    const suffix = businessUnitId ? `?business_unit_id=${businessUnitId}` : "";
    navigate(`${basePath}${suffix}`);
  };

  return (
    <div
      ref={frontDeskRef}
      className={`fd ${isLightTheme ? "fd--light" : "fd--dark"}`}
      style={{ "--fd-adaptive-scale": String(frontDeskScale) }}
    >
      <div className="fd-shell">
        {!businessUnitId ? (
          <StateCard title={copy.noBusinessUnitTitle} copy={copy.noBusinessUnitCopy} />
        ) : !canViewCheckIns && !canViewCheckOuts ? (
          <StateCard title={copy.permissionTitle} copy={copy.permissionCopy} />
        ) : isLoading ? (
          <StateCard title={copy.loadingTitle} copy={copy.loadingCopy} />
        ) : loadError ? (
          <StateCard title={copy.errorTitle} copy={copy.errorCopy} />
        ) : (
          <section className="fd-card fd-listCard">
              <div className="fd-listHeader">
                <div className="fd-listHeadMain">
                  <span className="fd-eyebrow">{copy.section}</span>
                  <h2 className="fd-listTitle">{activeTitle}</h2>
                  <p className="fd-listMeta">
                    {copy.visibleCount(board.rows.length, board.totalRows)} | {activeSubtitle}
                  </p>
                </div>

                <div className="fd-listTools">
                  {canViewCheckIns && canViewCheckOuts ? (
                    <div className="fd-segment" role="tablist" aria-label={copy.section}>
                      <button
                        type="button"
                        className={`fd-segmentBtn ${resolvedTab === "check-in" ? "is-active" : ""}`}
                        onClick={() => openTab("check-in")}
                      >
                        {copy.checkIns}
                      </button>
                      <button
                        type="button"
                        className={`fd-segmentBtn ${resolvedTab === "in-house" ? "is-active" : ""}`}
                        onClick={() => openTab("in-house")}
                      >
                        {copy.inHouse}
                      </button>
                      <button
                        type="button"
                        className={`fd-segmentBtn ${resolvedTab === "check-out" ? "is-active" : ""}`}
                        onClick={() => openTab("check-out")}
                      >
                        {copy.checkOuts}
                      </button>
                    </div>
                  ) : null}

                  {resolvedTab === "in-house" ? (
                    <button
                      type="button"
                      className="fd-toggleBtn"
                      onClick={openOccupancyGrid}
                    >
                      {copy.occupancyGridAction}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="fd-toggleBtn"
                    onClick={handleToggleOtherDates}
                  >
                    {showOtherDates ? copy.hideOtherDates : copy.showOtherDates}
                  </button>
                </div>
              </div>

              {showOtherDates ? (
                <div className="fd-rangePanels">
                  <div className="fd-rangePanel">
                    <div className="fd-rangeTitle">{copy.dateRangeLabel}</div>
                    <label className="fd-dateField">
                      <span>{copy.dateFrom}</span>
                      <input
                        ref={dateFromInputRef}
                        type="date"
                        className="fd-dateInput"
                        value={dateRange.from}
                        onChange={(event) =>
                          setDateRange((current) => ({
                            ...current,
                            from: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className="fd-dateField">
                      <span>{copy.dateTo}</span>
                      <input
                        type="date"
                        className="fd-dateInput"
                        value={dateRange.to}
                        onChange={(event) =>
                          setDateRange((current) => ({
                            ...current,
                            to: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                </div>
              ) : null}

              <div className="fd-listSubbar">
                <div className="fd-metrics">
                  <span className="fd-pill">
                    {copy.visible} <strong>{board.rows.length}</strong>
                  </span>
                  <span className="fd-pill fd-pill--today">
                    {copy.today} <strong>{board.todayCount}</strong>
                  </span>
                  <span className="fd-pill fd-pill--warn">
                    {copy.overdue} <strong>{board.overdueCount}</strong>
                  </span>
                  <span className="fd-pill">
                    {copy.upcoming} <strong>{board.upcomingCount}</strong>
                  </span>
                  <span className="fd-pill">
                    {copy.future} <strong>{board.futureCount}</strong>
                  </span>
                  {showOtherDates ? (
                    <span className="fd-pill fd-pill--hidden">
                      {copy.dateRangeLabel} <strong>{activeDateBounds.from} → {activeDateBounds.to}</strong>
                    </span>
                  ) : null}
                </div>

                <div className="fd-listMeta">{copy.rowCount(board.totalRows)}</div>
              </div>

              <div className="fd-wrap">
                <table className="fd-table">
                  <colgroup>
                    <col style={{ width: "250px" }} />
                    <col style={{ width: "130px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "120px" }} />
                    <col style={{ width: "112px" }} />
                    <col style={{ width: "122px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "110px" }} />
                    <col style={{ width: "210px" }} />
                    <col style={{ width: "140px" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th className="fd-sticky">{copy.reservation}</th>
                      <th>{copy.room}</th>
                      <th>{copy.checkIn}</th>
                      <th>{copy.checkOut}</th>
                      <th>{copy.occupancy}</th>
                      <th>{copy.status}</th>
                      <th>{copy.total}</th>
                      <th>{copy.balance}</th>
                      <th>{copy.readiness}</th>
                      <th className="fd-actionHead">{copy.action}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {board.rows.length === 0 ? (
                      <tr className="fd-emptyRow">
                        <td colSpan={10}>
                          <StateCard
                            title={showOtherDates ? copy.emptyDateRangeTitle : copy.emptyTitle}
                            copy={showOtherDates ? copy.emptyDateRangeCopy : copy.emptyCopy}
                          />
                        </td>
                      </tr>
                    ) : (
                      board.rows.map((row) => {
                        const dateClass = `fd-datePill fd-datePill--${row.bucket}`;
                        const missingItems = row.checkInReadiness?.missing || [];
                        const previewMissingItems = missingItems.slice(0, 3);
                        const hiddenMissingCount = Math.max(
                          missingItems.length - previewMissingItems.length,
                          0
                        );

                        return (
                          <tr key={`${row.reservationId}-${row.locator}`}>
                            <td className="fd-sticky">
                              <div className="fd-resCell">
                                <button
                                  type="button"
                                  className="fd-locatorBtn"
                                  onClick={() => openReservation(row)}
                                  disabled={!canViewReservationDetails}
                                  title={copy.openReservation}
                                >
                                  {row.locator}
                                </button>

                                <div className="fd-guest">{row.guestName}</div>
                                <div className="fd-subMeta">
                                  {row.roomCategory} | {copy.nights(row.nights)}
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="fd-roomValue">{row.roomNumber}</div>
                              <div className="fd-subMeta">{row.roomCategory}</div>
                            </td>

                            <td>
                              <span className={dateClass}>
                                {formatDateLabel(row.checkInDate, locale, copy.noDate)}
                              </span>
                            </td>

                            <td>
                              <span className={dateClass}>
                                {formatDateLabel(row.checkOutDate, locale, copy.noDate)}
                              </span>
                            </td>

                            <td>
                              <div className="fd-occupancy">
                                <span>
                                  {copy.adultsShort}: {row.adults}
                                </span>
                                <span>
                                  {copy.childrenShort}: {row.children}
                                </span>
                                <span>
                                  {copy.babiesShort}: {row.babies}
                                </span>
                              </div>
                            </td>

                            <td>
                              <span className={`fd-status fd-status--${row.statusTone}`}>{row.statusLabel}</span>
                            </td>

                            <td className="fd-money">
                              {formatFromBase(row.total, { locale })}
                            </td>

                            <td className="fd-money fd-money--balance">
                              {formatFromBase(row.balance, { locale })}
                            </td>

                            <td className="fd-readinessCell">
                              {missingItems.length > 0 ? (
                                <div
                                  className="fd-readiness"
                                  title={row.checkInReadiness?.title || undefined}
                                >
                                  {previewMissingItems.map((item) => (
                                    <span key={item.key} className="fd-readinessChip">
                                      {item.label}
                                    </span>
                                  ))}
                                  {hiddenMissingCount > 0 ? (
                                    <span className="fd-readinessChip fd-readinessChip--more">
                                      {copy.moreMissing(hiddenMissingCount)}
                                    </span>
                                  ) : null}
                                </div>
                              ) : (
                                <span className="fd-readinessReady">
                                  {copy.readyForCheckIn}
                                </span>
                              )}
                            </td>

                            <td className="fd-actionCell">
                              <div className="fd-actionWrap">
                                <button
                                  type="button"
                                  className="fd-actionBtn fd-actionBtn--primary"
                                  onClick={() => openReservationActions(row)}
                                  disabled={!row.reservationGroupId || !canViewReservationDetails}
                                  title={
                                    !canViewReservationDetails
                                      ? "You do not have permission to open reservation actions"
                                      : row.reservationGroupId
                                        ? "Open reservation actions"
                                        : "Reservation actions are unavailable without a reservation group"
                                  }
                                >
                                  {reservationActionsLauncher?.reservationId === row.reservationId &&
                                  !reservationActionsLauncher?.isReady
                                    ? copy.loadingActions
                                    : (
                                        <>
                                          <FiMoreHorizontal size={14} aria-hidden="true" />
                                          Actions
                                        </>
                                      )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </section>
        )}
      </div>
      {reservationActionsLauncher ? (
        <ReservationGroupView
          key={reservationActionsLauncher.sessionId}
          reservationGroupId={reservationActionsLauncher.reservationGroupId}
          initialReservationActionsId={reservationActionsLauncher.reservationId}
          actionsOnly
          readOnlyPreview={demoReadOnlyActive}
          onActionsOpen={() => {
            setReservationActionsLauncher((current) =>
              current?.sessionId === reservationActionsLauncher.sessionId
                ? { ...current, isReady: true }
                : current
            );
          }}
          onActionsClose={() => setReservationActionsLauncher(null)}
          onActionsError={(error) => {
            setReservationActionsLauncher(null);
            window.alert(
              error?.response?.data?.error || "Could not load reservation actions."
            );
          }}
          onReservationChange={() => {
            setReservationActionsRefreshToken((current) => current + 1);
          }}
        />
      ) : null}
    </div>
  );
}
